import type { SelectOptionType } from '../../Controls/Select/types';

/**
 * Structural equality for static dataSource options.
 *
 * The items reconcile effect uses this to decide whether a new `options` array
 * reference actually carries different content. Comparing content (not
 * reference) keeps both consumer patterns safe at once:
 *   - memoized redux-fed arrays: a reference change means a content change →
 *     options are re-read (plan "Items prop reconciliation");
 *   - inline literals rebuilt every render: new references, equal content →
 *     no-op, so propagation can never enter a dispatch → render → dispatch loop.
 *
 * Compared per option node: every own scalar prop (`value`, `disabled`,
 * string/number `label`, custom flags like `current`) plus the nested
 * `options` / `children` structure, order-sensitive. Non-primitive props
 * (ReactNode labels, meta objects, callbacks) are skipped — inline consumers
 * rebuild those with fresh identity every render, and comparing them would
 * defeat the no-op guarantee.
 */
export function staticOptionsEqual(a: SelectOptionType[], b: SelectOptionType[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!optionEqual(a[i], b[i])) return false;
  }
  return true;
}

/** Scalars comparable across renders; objects/functions get fresh identity when inlined. */
function isComparableScalar(v: unknown): boolean {
  return v === null || (typeof v !== 'object' && typeof v !== 'function');
}

/** Skip both-non-scalar pairs (ReactNode labels, meta objects, callbacks). */
function scalarPropEqual(av: unknown, bv: unknown): boolean {
  if (!isComparableScalar(av) && !isComparableScalar(bv)) return true;
  return Object.is(av, bv);
}

const hasOwn = Object.prototype.hasOwnProperty;

// Two passes over own keys instead of a merged-key Set — same semantics,
// no per-node Set/spread allocations (this runs per option on every items
// identity change when the reference fast path misses).
function optionEqual(a: SelectOptionType, b: SelectOptionType): boolean {
  if (a === b) return true;
  for (const key of Object.keys(a)) {
    if (key === 'options' || key === 'children') continue;
    if (!scalarPropEqual(a[key], b[key])) return false;
  }
  for (const key of Object.keys(b)) {
    if (key === 'options' || key === 'children') continue;
    if (hasOwn.call(a, key)) continue; // compared in the first pass
    if (!scalarPropEqual(a[key], b[key])) return false;
  }
  return nestedEqual(a.options, b.options) && nestedEqual(a.children, b.children);
}

function nestedEqual(a?: SelectOptionType[], b?: SelectOptionType[]): boolean {
  if (a === b) return true;
  if (!a || !b) return (a?.length ?? 0) === (b?.length ?? 0);
  return staticOptionsEqual(a, b);
}
